import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users } from "lucide-react";
import { track, buildBookingLink } from "@/lib/analytics";

export default function AvailabilityWidget({ compact = false }) {
  const today = new Date().toISOString().split("T")[0];
  const [checkin, setCheckin] = useState("");
  const [checkout, setCheckout] = useState("");
  const [guests, setGuests] = useState(2);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const directUrl = () => {
    const params = new URLSearchParams();
    if (checkin) params.set("checkin", checkin);
    if (checkout) params.set("checkout", checkout);
    if (guests) params.set("guests", guests);
    return `/book?${params.toString()}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    track("availability_search", { checkin, checkout, guests });
    setBusy(true);
    try {
      const res = await buildBookingLink({ checkin, checkout, guests });
      if (res.method === "booking_engine" && res.url) {
        track("booking_engine_click", { checkin, checkout, guests });
        window.open(res.url, "_blank", "noopener");
      } else {
        navigate(directUrl());
      }
    } catch {
      navigate(directUrl());
    } finally {
      setBusy(false);
    }
  };

  const fieldCls = "w-full bg-transparent text-sm text-cream placeholder-cream-dim focus:outline-none";
  const labelCls = "block text-[10px] font-mono uppercase tracking-[0.2em] text-copper-400 mb-1";

  return (
    <form
      onSubmit={submit}
      id="availability-widget"
      data-testid="availability-widget"
      className={`glass copper-glow rounded-2xl ${compact ? "p-4" : "p-5 sm:p-6"}`}
      aria-label="Check availability"
    >
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-[1fr_1fr_auto_auto] sm:items-end">
        <div className="border-b border-white/10 pb-2">
          <label htmlFor="hero-check-in" className={labelCls}>Check-in</label>
          <input id="hero-check-in" data-testid="hero-check-in-date" type="date" min={today} value={checkin}
            onChange={(e) => setCheckin(e.target.value)} className={`${fieldCls} [color-scheme:dark]`} />
        </div>
        <div className="border-b border-white/10 pb-2">
          <label htmlFor="hero-check-out" className={labelCls}>Check-out</label>
          <input id="hero-check-out" data-testid="hero-check-out-date" type="date" min={checkin || today} value={checkout}
            onChange={(e) => setCheckout(e.target.value)} className={`${fieldCls} [color-scheme:dark]`} />
        </div>
        <div className="border-b border-white/10 pb-2">
          <label htmlFor="hero-guests" className={labelCls}>Guests</label>
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-copper-300" />
            <select id="hero-guests" data-testid="hero-guests-select" value={guests} onChange={(e) => setGuests(e.target.value)} className={`${fieldCls} cursor-pointer`}>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n} className="bg-charcoal-800">{n} {n === 1 ? "guest" : "guests"}</option>)}
            </select>
          </div>
        </div>
        <button type="submit" disabled={busy} data-testid="hero-submit-search-btn"
          className="col-span-2 sm:col-span-1 rounded-full bg-copper px-7 py-3.5 text-xs font-mono uppercase tracking-[0.18em] text-charcoal transition-all duration-300 hover:bg-copper-300 hover:shadow-[0_0_30px_rgba(200,125,85,0.4)] disabled:opacity-60">
          {busy ? "Checking…" : "Check Availability"}
        </button>
      </div>
      <p className="mt-3 text-[11px] font-light text-cream-dim">
        Opens our official booking engine with your dates pre-filled.{" "}
        <a href="/book" onClick={(e) => { e.preventDefault(); track("book_direct_click"); navigate(directUrl()); }}
          data-testid="book-direct-upi-link" className="text-copper-300 underline-offset-2 transition-colors hover:text-copper-400 hover:underline">
          Or book direct &amp; pay via UPI
        </a>
      </p>
    </form>
  );
}
